import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
  Paper,
  LinearProgress,
  Box,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import logo from "../../assets/logostx.jpg";
import API from "../../services/api";
import { useAuth } from "../../auth/AuthProvider";

function Login() {
  const navigate = useNavigate();
  const { signinAndBootstrap } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleChangeCredentials = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: name === "username" ? value.toLowerCase() : value,
    });
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLoggingIn || cooldown > 0) return; // Evitar submits durante login/cooldown

    setError("");
    setIsLoggingIn(true);

    try {
      const [_loginResponse, tokenResponse] = await Promise.all([
        API.post("auth/login/", credentials),
        API.post("auth/token/", credentials),
      ]);
      const userData = {
        username: credentials.username,
        first_name: tokenResponse.data.first_name,
        last_name: tokenResponse.data.last_name,
        email: tokenResponse.data.email,
        avatar: tokenResponse.data?.user?.avatar || tokenResponse.data?.avatar || null,
        ...tokenResponse.data.user,
      };
      console.log(tokenResponse.data);
      localStorage.setItem("userData", JSON.stringify(userData));
      await signinAndBootstrap({
        access: tokenResponse.data.access,
        refresh: tokenResponse.data.refresh,
      });
      setAttempts(0);
      navigate("/home", { replace: true });
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data || {};
      const detail = typeof data?.detail === "string" ? data.detail : "";
      const nonFieldErrors = Array.isArray(data?.non_field_errors)
        ? data.non_field_errors.join(" ")
        : data?.non_field_errors;
      const nextAttempts = attempts + 1;
      const throttled =
        status === 429 ||
        (typeof detail === "string" && detail.toLowerCase().includes("throttl"));

      if (throttled) {
        setError("Demasiados intentos. Vuelve a intentarlo en un momento.");
        setCooldown((prev) => (prev > 0 ? prev : 30));
        setAttempts(nextAttempts);
      } else if (!error.response || [500, 502, 503, 504].includes(status)) {
        setError("No se pudo conectar con el servidor. Inténtalo de nuevo más tarde.");
      } else {
        const message =
          (typeof nonFieldErrors === "string" && nonFieldErrors) ||
          detail ||
          "Credenciales incorrectas";
        setError(message || "Credenciales incorrectas");
        setAttempts(nextAttempts);
      }
      console.error(error);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <form onSubmit={handleLogin} style={{ width: "100%" }}>
      {/* Línea de carga minimalista en la parte superior */}
      {isLoggingIn && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
          }}
        >
          <LinearProgress />
        </Box>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(180deg, #10173c, #1e2a5a)",
          padding: "20px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <Paper
            elevation={6}
            sx={{
              padding: "30px",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "15px",
              width: "90vw",
              maxWidth: "280px",
              minWidth: "240px",
              backdropFilter: "blur(20px)",
              margin: "0 auto",
            }}
          >
            <img
              src={logo}
              alt="Logo"
              width={90}
              style={{ borderRadius: "5px" }}
            />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Iniciar sesión
            </Typography>

            <motion.div
              whileFocus={{ scale: 1.05 }}
              whileHover={{ scale: 1.02 }}
              style={{ width: "100%" }}
            >
              <FormControl
                variant="outlined"
                fullWidth
                sx={{
                  "& input": {
                    padding: "12px 14px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "black",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                    width: "100%",
                    boxSizing: "border-box",
                  },
                  "& input:not(:placeholder-shown), &:focus-within input": {
                    backgroundColor: "rgba(16, 14, 14, 0.2)",
                    boxShadow: "0px 0px 8px rgba(255,255,255,0.3)",
                  },
                }}
              >
                <input
                  type="text"
                  placeholder="Usuario"
                  name="username"
                  required
                  onChange={handleChangeCredentials}
                  style={{ border: "none", outline: "none" }}
                  disabled={isLoggingIn || cooldown > 0}
                />
              </FormControl>
            </motion.div>

            <motion.div
              whileFocus={{ scale: 1.05 }}
              whileHover={{ scale: 1.02 }}
              style={{ width: "100%" }}
            >
              <FormControl
                variant="outlined"
                fullWidth
                required={true}
                sx={{
                  "& input": {
                    padding: "12px 14px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "black",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                    width: "100%",
                    boxSizing: "border-box",
                  },
                  "& input:not(:placeholder-shown), &:focus-within input": {
                    backgroundColor: "rgba(16, 14, 14, 0.2)",
                    boxShadow: "0px 0px 8px rgba(255,255,255,0.3)",
                  },
                }}
              >
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Contraseña"
                  name="password"
                  required
                  onChange={handleChangeCredentials}
                  style={{ border: "none", outline: "none" }}
                  disabled={isLoggingIn || cooldown > 0}
                />
                <InputAdornment
                  position="end"
                  sx={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                    disabled={isLoggingIn || cooldown > 0}
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ color: "black" }} />
                    ) : (
                      <Visibility sx={{ color: "black" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              </FormControl>
            </motion.div>

            {/* Error bonito y animado */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                style={{ width: "100%" }}
              >
                <Alert
                  severity="error"
                  variant="filled"
                  sx={{
                    borderRadius: "8px",
                    fontSize: "11px",
                    py: 0.5,
                    px: 1.25,
                  }}
                >
                  {error}
                </Alert>
              </motion.div>
            )}

            <Button
              variant="contained"
              color="secondary"
              type="submit"
              sx={{
                fontSize: "11px",
                padding: "10px",
                borderRadius: "8px",
                width: "100%",
              }}
              disabled={isLoggingIn || cooldown > 0}
            >
              {cooldown > 0
                ? `Espera ${cooldown}s`
                : isLoggingIn
                ? "Ingresando..."
                : "Iniciar sesión"}
            </Button>

            <Typography variant="body2" sx={{ marginTop: "5px" }}>
              o
            </Typography>

            <Button
              variant="contained"
              color="primary"
              sx={{
                fontSize: "11px",
                padding: "10px",
                borderRadius: "8px",
                width: "100%",
              }}
              onClick={() => navigate("/register")}
              disabled={isLoggingIn || cooldown > 0}
            >
              Registrarse
            </Button>
          </Paper>
        </motion.div>
      </motion.div>
    </form>
  );
}

export default Login;
